import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useCreatePrice, usePrices, useUpdatePrice } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Prices that are the same for every customer. Marker prices live on the
 * biomarker, draw fees on the lab; what is left is our own work, and this is
 * where its rate is set.
 *
 * Rates the code looks up by key. Listing them here means a rate that has no
 * row yet — production runs migrations, not seeds — can be given one without
 * anybody typing the key by hand and silently missing the lookup.
 */
const KNOWN_PRICES = [
  {
    key: "doctors_assessment_cost",
    label: "Legevurdering per blodprøve",
    description:
      "Hva det koster oss at en lege skriver vurderingen for én gjennomført blodprøve, i tillegg til prøvetaking og biomarkører. Brukes av både Auction House og Lønnsomhet-dashboardet.",
  },
] as const;

export function PricesPage() {
  const { data: prices, isLoading } = usePrices();
  const updatePrice = useUpdatePrice();
  const createPrice = useCreatePrice();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newDrafts, setNewDrafts] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Laster…
      </div>
    );
  }

  const rows = prices ?? [];
  const missing = KNOWN_PRICES.filter(
    (known) => !rows.some((r) => r.key === known.key)
  );

  const save = async (id: string, key: string) => {
    const raw = (drafts[id] ?? "").trim().replace(",", ".");
    if (!raw || !(Number(raw) >= 0)) {
      toast.error("Prisen må være et positivt tall");
      return;
    }
    try {
      await updatePrice.mutateAsync({ id, data: { price: raw } });
      setDrafts((d) => {
        const { [id]: _, ...rest } = d;
        return rest;
      });
      toast.success(`${key} lagret`);
    } catch (e: any) {
      toast.error(e?.message ?? "Kunne ikke lagre");
    }
  };

  const create = async (known: (typeof KNOWN_PRICES)[number]) => {
    const raw = (newDrafts[known.key] ?? "").trim().replace(",", ".");
    if (!raw || !(Number(raw) >= 0)) {
      toast.error("Prisen må være et positivt tall");
      return;
    }
    try {
      await createPrice.mutateAsync({ ...known, price: raw });
      setNewDrafts((d) => {
        const { [known.key]: _, ...rest } = d;
        return rest;
      });
      toast.success(`${known.label} lagt inn`);
    } catch (e: any) {
      toast.error(e?.message ?? "Kunne ikke lagre");
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-admin-text-subtle">
        Satser som er like for alle kunder. Markørpriser ligger på biomarkøren
        og prøvetakingsgebyr på laben — det som står igjen er arbeidet vi gjør
        selv, og det koster det samme uansett hvem kunden er.
      </p>
      {missing.map((known) => (
        <Card key={known.key} className="border-admin-warn-400/40">
          <CardContent className="p-4 flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-[16rem]">
              <p className="font-medium text-admin-text-primary">
                {known.label}
              </p>
              <p className="text-xs text-admin-warn-400 mt-1">
                Ikke lagt inn ennå — regnes som 0 kr, og både Auction House og
                Lønnsomhet sier fra om det.
              </p>
              <p className="text-xs text-admin-text-subtle mt-1">
                {known.description}
              </p>
              <p className="text-xs text-admin-text-subtle mt-1 font-mono">
                {known.key}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                inputMode="decimal"
                className="w-32 text-right tabular-nums"
                placeholder="0"
                value={newDrafts[known.key] ?? ""}
                onChange={(e) =>
                  setNewDrafts((d) => ({ ...d, [known.key]: e.target.value }))
                }
              />
              <span className="text-sm text-admin-text-subtle">kr</span>
              <Button
                size="sm"
                disabled={createPrice.isPending}
                onClick={() => create(known)}
              >
                <Save className="h-4 w-4" />
                Legg inn
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {rows.map((price) => {
        const draft = drafts[price.id];
        const dirty = draft !== undefined && draft !== price.price;
        return (
          <Card key={price.id}>
            <CardContent className="p-4 flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-[16rem]">
                <p className="font-medium text-admin-text-primary">
                  {price.label}
                </p>
                {price.description && (
                  <p className="text-xs text-admin-text-subtle mt-1">
                    {price.description}
                  </p>
                )}
                <p className="text-xs text-admin-text-subtle mt-1 font-mono">
                  {price.key}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  inputMode="decimal"
                  className="w-32 text-right tabular-nums"
                  value={draft ?? price.price}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [price.id]: e.target.value }))
                  }
                />
                <span className="text-sm text-admin-text-subtle">kr</span>
                <Button
                  size="sm"
                  disabled={!dirty || updatePrice.isPending}
                  onClick={() => save(price.id, price.label)}
                >
                  <Save className="h-4 w-4" />
                  Lagre
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
